import React from 'react';
import {
  Box,
  Typography,
  Container,
  Card,
  CardContent,
  IconButton,
  Button,
  Paper,
  useTheme,
  useMediaQuery,
  Fade,
  Zoom,
  Grid,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  ArrowBack,
  Search,
  CheckCircle,
  Schedule,
  Star,
  LocalOffer,
  Security,
  Support,
  Favorite,
  Spa,
  ContentCut,
  Face,
  ShoppingBag,
  Payment,
  EmojiEvents,
  AutoAwesome,
  Verified,
  TrendingUp,
  Stars,
  CalendarMonth,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import BottomNav from '../components/BottomNavigation';
import useResponsive from '../hooks/useResponsive';

// Styled Components
const HeroSection = styled(Box)(({ theme }) => ({
  background: 'linear-gradient(135deg, #0A2A66 0%, #1a237e 50%, #6366f1 100%)',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'radial-gradient(circle at 20% 50%, rgba(99, 102, 241, 0.3) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(99, 102, 241, 0.3) 0%, transparent 50%)',
    animation: 'pulse 8s ease-in-out infinite',
  },
  '@keyframes pulse': {
    '0%, 100%': { opacity: 0.8 },
    '50%': { opacity: 1 },
  },
}));

const FeatureCard = styled(Card)(({ theme }) => ({
  height: '100%',
  borderRadius: 16,
  transition: 'all 0.3s ease',
  border: '1px solid rgba(26, 35, 126, 0.1)',
  background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: '0 12px 40px rgba(26, 35, 126, 0.15)',
    borderColor: '#1a237e',
  },
}));

const ValueCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  borderRadius: 20,
  textAlign: 'center',
  background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
  border: '2px solid',
  borderColor: 'transparent',
  transition: 'all 0.3s ease',
  '&:hover': {
    borderColor: '#6366f1',
    transform: 'scale(1.05)',
    boxShadow: '0 20px 60px rgba(99, 102, 241, 0.2)',
  },
}));

const StatBox = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  padding: theme.spacing(3),
  borderRadius: 16,
  background: 'rgba(255, 255, 255, 0.1)',
  backdropFilter: 'blur(10px)',
  border: '1px solid rgba(255, 255, 255, 0.2)',
  transition: 'all 0.3s ease',
  '&:hover': {
    background: 'rgba(255, 255, 255, 0.2)',
    transform: 'scale(1.05)',
  },
}));

const AboutUs = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const features = [
    {
      icon: <Search sx={{ fontSize: 40 }} />,
      title: 'Easy Discovery',
      description: 'Find the perfect beauty, hair, and wellness professionals in your area with our intuitive search and filters.',
      color: '#6366f1',
    },
    {
      icon: <CalendarMonth sx={{ fontSize: 40 }} />,
      title: 'Instant Booking',
      description: 'Book appointments 24/7 at your convenience. View real-time availability and confirm instantly.',
      color: '#8b5cf6',
    },
    {
      icon: <Verified sx={{ fontSize: 40 }} />,
      title: 'Verified Professionals',
      description: 'All service providers are thoroughly vetted and verified to ensure quality and safety.',
      color: '#ec4899',
    },
    {
      icon: <Payment sx={{ fontSize: 40 }} />,
      title: 'Secure Payments',
      description: 'Pay securely through our platform with multiple payment options. Your financial data is protected.',
      color: '#f59e0b',
    },
    {
      icon: <ShoppingBag sx={{ fontSize: 40 }} />,
      title: 'Shop Products',
      description: 'Browse and purchase professional beauty products directly from your favorite service providers.',
      color: '#10b981',
    },
    {
      icon: <Support sx={{ fontSize: 40 }} />,
      title: '24/7 Support',
      description: 'Our customer support team is always available to help you with any questions or concerns.',
      color: '#3b82f6',
    },
  ];

  const benefits = [
    {
      icon: <Star sx={{ fontSize: 50 }} />,
      title: 'Quality Guaranteed',
      description: 'Read reviews, compare ratings, and choose from the best professionals in your area.',
    },
    {
      icon: <Schedule sx={{ fontSize: 50 }} />,
      title: 'Save Time',
      description: 'No more phone calls or waiting. Book your appointments in seconds from anywhere.',
    },
    {
      icon: <LocalOffer sx={{ fontSize: 50 }} />,
      title: 'Exclusive Deals',
      description: 'Get access to special offers, discounts, and loyalty rewards on services and products.',
    },
    {
      icon: <Favorite sx={{ fontSize: 50 }} />,
      title: 'Personalized',
      description: 'Save your favorite providers, track your appointments, and manage your beauty routine effortlessly.',
    },
  ];

  const timeline = [
    { 
      year: '2023', 
      title: 'Problem Identified', 
      description: 'Customers struggling to find reliable beauty, hair, and wellness professionals in their area' 
    },
    { 
      year: '2023', 
      title: 'Solution Created', 
      description: 'NIStylez platform conceived to connect customers with verified professionals easily' 
    },
    { 
      year: '2024', 
      title: 'Platform Built', 
      description: 'Developed a seamless booking experience with integrated payments and product shopping' 
    },
    { 
      year: '2024', 
      title: 'Community Grows', 
      description: '100+ verified professionals joined, thousands of successful appointments completed' 
    },
    { 
      year: '2025', 
      title: 'Launch & Expansion', 
      description: 'Official launch helping customers discover and book quality beauty services effortlessly' 
    },
  ];

  return (
    <Box sx={{ backgroundColor: '#f8fafc', minHeight: '100vh', pb: 10 }}>
      {/* Header */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #0A2A66 0%, #1a237e 100%)',
          borderBottom: 1,
          borderColor: 'divider',
          py: 2,
          position: 'sticky',
          top: 0,
          zIndex: 100,
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
        }}
      >
        <Container maxWidth="lg">
          <Box display="flex" alignItems="center" gap={2}>
            <IconButton onClick={() => navigate(-1)} sx={{ color: 'white' }}>
              <ArrowBack />
            </IconButton>
            <Typography variant="h5" fontWeight="600" color="white">
              About NIStylez
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Hero Section */}
      <HeroSection sx={{ py: { xs: 8, md: 12 }, position: 'relative' }}>
        <Container maxWidth="lg">
          <Fade in timeout={800}>
            <Box textAlign="center" sx={{ position: 'relative', zIndex: 1 }}>
              <Typography
                variant={isMobile ? 'h3' : 'h2'}
                color="white"
                fontWeight="800"
                gutterBottom
                sx={{
                  textShadow: '0 2px 20px rgba(0,0,0,0.3)',
                  mb: 2,
                }}
              >
                Discover Your Perfect Beauty Experience
              </Typography>
              <Typography
                variant={isMobile ? 'h6' : 'h5'}
                color="white"
                sx={{
                  opacity: 0.95,
                  mb: 4,
                  maxWidth: 700,
                  mx: 'auto',
                  textShadow: '0 1px 10px rgba(0,0,0,0.2)',
                }}
              >
                Connect with trusted beauty, hair, and wellness professionals. Book instantly, shop products, and enjoy premium services.
              </Typography>
              
              {/* Hero Stats */}
              <Grid container spacing={3} justifyContent="center" sx={{ mt: 6 }}>
                <Grid item xs={6} md={3}>
                  <StatBox>
                    <Typography variant="h3" fontWeight="bold" color="white">
                      100+
                    </Typography>
                    <Typography variant="body1" color="white" sx={{ opacity: 0.9 }}>
                      Verified Professionals
                    </Typography>
                  </StatBox>
                </Grid>
                <Grid item xs={6} md={3}>
                  <StatBox>
                    <Typography variant="h3" fontWeight="bold" color="white">
                      50K+
                    </Typography>
                    <Typography variant="body1" color="white" sx={{ opacity: 0.9 }}>
                      Happy Customers
                    </Typography>
                  </StatBox>
                </Grid>
                <Grid item xs={6} md={3}>
                  <StatBox>
                    <Typography variant="h3" fontWeight="bold" color="white">
                      4.9★
                    </Typography>
                    <Typography variant="body1" color="white" sx={{ opacity: 0.9 }}>
                      Average Rating
                    </Typography>
                  </StatBox>
                </Grid>
                <Grid item xs={6} md={3}>
                  <StatBox>
                    <Typography variant="h3" fontWeight="bold" color="white">
                      24/7
                    </Typography>
                    <Typography variant="body1" color="white" sx={{ opacity: 0.9 }}>
                      Booking Available
                    </Typography>
                  </StatBox>
                </Grid>
              </Grid>
            </Box>
          </Fade>
        </Container>
      </HeroSection>

      <Container maxWidth="lg" sx={{ py: 6 }}>
        {/* Mission Section */}
        <Fade in timeout={1000}>
          <Card
            sx={{
              mb: 6,
              borderRadius: 4,
              overflow: 'hidden',
              background: 'linear-gradient(135deg, #ffffff 0%, #f0f9ff 100%)',
              border: '2px solid',
              borderColor: '#e0e7ff',
            }}
          >
            <CardContent sx={{ p: { xs: 3, md: 6 } }}>
              <Box textAlign="center" mb={4}>
                <Stars sx={{ fontSize: 60, color: '#6366f1', mb: 2 }} />
                <Typography variant="h3" fontWeight="bold" color="#1a237e" gutterBottom>
                  Our Mission
                </Typography>
              </Box>
              <Typography
                variant="h6"
                sx={{
                  textAlign: 'center',
                  lineHeight: 1.8,
                  color: 'text.primary',
                  maxWidth: 900,
                  mx: 'auto',
                  fontSize: { xs: '1rem', md: '1.25rem' },
                }}
              >
                To make beauty, hair, and wellness services accessible to everyone by connecting customers with 
                trusted professionals. We believe in empowering you to look and feel your best with convenient, 
                reliable, and high-quality services at your fingertips.
              </Typography>
            </CardContent>
          </Card>
        </Fade>

        {/* Features Section */}
        <Box sx={{ mb: 8 }}>
          <Typography
            variant="h3"
            textAlign="center"
            fontWeight="bold"
            color="#1a237e"
            sx={{ mb: 2 }}
          >
            Why Choose NIStylez?
          </Typography>
          <Typography
            variant="h6"
            textAlign="center"
            color="text.secondary"
            sx={{ mb: 6, maxWidth: 700, mx: 'auto' }}
          >
            Everything you need for your beauty and wellness journey
          </Typography>

          <Grid container spacing={3}>
            {features.map((feature, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Zoom in timeout={500 + index * 100}>
                  <FeatureCard>
                    <CardContent sx={{ p: 3 }}>
                      <Box
                        sx={{
                          width: 80,
                          height: 80,
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          background: `linear-gradient(135deg, ${feature.color} 0%, ${feature.color}dd 100%)`,
                          mb: 3,
                          mx: 'auto',
                          boxShadow: `0 8px 24px ${feature.color}40`,
                        }}
                      >
                        {React.cloneElement(feature.icon, {
                          sx: { fontSize: 40, color: 'white' },
                        })}
                      </Box>
                      <Typography
                        variant="h6"
                        fontWeight="700"
                        color="#1a237e"
                        gutterBottom
                        textAlign="center"
                      >
                        {feature.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" textAlign="center">
                        {feature.description}
                      </Typography>
                    </CardContent>
                  </FeatureCard>
                </Zoom>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Benefits Section */}
        <Box sx={{ mb: 8, py: 6, borderRadius: 4, background: 'linear-gradient(135deg, #f0f9ff 0%, #e0e7ff 100%)' }}>
          <Container maxWidth="md">
            <Typography
              variant="h3"
              textAlign="center"
              fontWeight="bold"
              color="#1a237e"
              sx={{ mb: 2 }}
            >
              Your Benefits
            </Typography>
            <Typography
              variant="h6"
              textAlign="center"
              color="text.secondary"
              sx={{ mb: 6 }}
            >
              What you get when you choose NIStylez
            </Typography>

            <Grid container spacing={4}>
              {benefits.map((benefit, index) => (
                <Grid item xs={12} sm={6} key={index}>
                  <Zoom in timeout={800 + index * 100}>
                    <ValueCard elevation={0}>
                      <Box sx={{ color: '#6366f1', mb: 2 }}>
                        {benefit.icon}
                      </Box>
                      <Typography variant="h5" fontWeight="700" color="#1a237e" gutterBottom>
                        {benefit.title}
                      </Typography>
                      <Typography variant="body1" color="text.secondary">
                        {benefit.description}
                      </Typography>
                    </ValueCard>
                  </Zoom>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        {/* How It Works Section */}
        <Box sx={{ mb: 8 }}>
          <Typography
            variant="h3"
            textAlign="center"
            fontWeight="bold"
            color="#1a237e"
            sx={{ mb: 6 }}
          >
            How It Works
          </Typography>

          <Grid container spacing={4}>
            <Grid item xs={12} md={3}>
              <Fade in timeout={600}>
                <Card sx={{ p: 3, textAlign: 'center', borderRadius: 3, height: '100%' }}>
                  <Box
                    sx={{
                      width: 60,
                      height: 60,
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '1.5rem',
                      fontWeight: 'bold',
                      mx: 'auto',
                      mb: 2,
                    }}
                  >
                    1
                  </Box>
                  <Search sx={{ fontSize: 50, color: '#6366f1', mb: 2 }} />
                  <Typography variant="h6" fontWeight="700" color="#1a237e" gutterBottom>
                    Search
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Browse professionals, services, and products in your area
                  </Typography>
                </Card>
              </Fade>
            </Grid>

            <Grid item xs={12} md={3}>
              <Fade in timeout={800}>
                <Card sx={{ p: 3, textAlign: 'center', borderRadius: 3, height: '100%' }}>
                  <Box
                    sx={{
                      width: 60,
                      height: 60,
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #8b5cf6 0%, #ec4899 100%)',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '1.5rem',
                      fontWeight: 'bold',
                      mx: 'auto',
                      mb: 2,
                    }}
                  >
                    2
                  </Box>
                  <CheckCircle sx={{ fontSize: 50, color: '#8b5cf6', mb: 2 }} />
                  <Typography variant="h6" fontWeight="700" color="#1a237e" gutterBottom>
                    Choose
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Compare ratings, reviews, and availability to find your perfect match
                  </Typography>
                </Card>
              </Fade>
            </Grid>

            <Grid item xs={12} md={3}>
              <Fade in timeout={1000}>
                <Card sx={{ p: 3, textAlign: 'center', borderRadius: 3, height: '100%' }}>
                  <Box
                    sx={{
                      width: 60,
                      height: 60,
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #ec4899 0%, #f59e0b 100%)',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '1.5rem',
                      fontWeight: 'bold',
                      mx: 'auto',
                      mb: 2,
                    }}
                  >
                    3
                  </Box>
                  <CalendarMonth sx={{ fontSize: 50, color: '#ec4899', mb: 2 }} />
                  <Typography variant="h6" fontWeight="700" color="#1a237e" gutterBottom>
                    Book
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Select your preferred time slot and confirm your appointment instantly
                  </Typography>
                </Card>
              </Fade>
            </Grid>

            <Grid item xs={12} md={3}>
              <Fade in timeout={1200}>
                <Card sx={{ p: 3, textAlign: 'center', borderRadius: 3, height: '100%' }}>
                  <Box
                    sx={{
                      width: 60,
                      height: 60,
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #f59e0b 0%, #10b981 100%)',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '1.5rem',
                      fontWeight: 'bold',
                      mx: 'auto',
                      mb: 2,
                    }}
                  >
                    4
                  </Box>
                  <AutoAwesome sx={{ fontSize: 50, color: '#10b981', mb: 2 }} />
                  <Typography variant="h6" fontWeight="700" color="#1a237e" gutterBottom>
                    Enjoy
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Relax and enjoy your premium beauty service experience
                  </Typography>
                </Card>
              </Fade>
            </Grid>
          </Grid>
        </Box>

        {/* Timeline Section */}
        <Box sx={{ mb: 8 }}>
          <Typography
            variant="h3"
            textAlign="center"
            fontWeight="bold"
            color="#1a237e"
            sx={{ mb: 6 }}
          >
            Our Journey
          </Typography>

          <Box sx={{ position: 'relative', pl: { xs: 2, md: 0 } }}>
            {timeline.map((item, index) => (
              <Fade in timeout={1000 + index * 200} key={index}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    mb: 4,
                    flexDirection: { xs: 'column', md: index % 2 === 0 ? 'row' : 'row-reverse' },
                  }}
                >
                  <Box
                    sx={{
                      flex: 1,
                      textAlign: { xs: 'center', md: index % 2 === 0 ? 'right' : 'left' },
                      pr: { md: index % 2 === 0 ? 4 : 0 },
                      pl: { md: index % 2 === 0 ? 0 : 4 },
                    }}
                  >
                    <Card
                      sx={{
                        p: 3,
                        borderRadius: 3,
                        background: 'white',
                        border: '2px solid #e0e7ff',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          borderColor: '#6366f1',
                          transform: 'scale(1.05)',
                        },
                      }}
                    >
                      <Typography variant="h4" fontWeight="bold" color="#6366f1" gutterBottom>
                        {item.year}
                      </Typography>
                      <Typography variant="h6" fontWeight="600" color="#1a237e" gutterBottom>
                        {item.title}
                      </Typography>
                      <Typography variant="body1" color="text.secondary">
                        {item.description}
                      </Typography>
                    </Card>
                  </Box>
                  <Box
                    sx={{
                      width: 20,
                      height: 20,
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                      border: '4px solid white',
                      boxShadow: '0 0 0 4px #6366f1',
                      my: { xs: 2, md: 0 },
                      flexShrink: 0,
                    }}
                  />
                  <Box sx={{ flex: 1 }} />
                </Box>
              </Fade>
            ))}
          </Box>
        </Box>

        {/* Service Categories Section */}
        <Box sx={{ mb: 8 }}>
          <Typography
            variant="h3"
            textAlign="center"
            fontWeight="bold"
            color="#1a237e"
            sx={{ mb: 2 }}
          >
            Services We Offer
          </Typography>
          <Typography
            variant="h6"
            textAlign="center"
            color="text.secondary"
            sx={{ mb: 6 }}
          >
            Comprehensive beauty and wellness solutions
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={6} md={3}>
              <Card
                sx={{
                  p: 3,
                  textAlign: 'center',
                  borderRadius: 3,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 12px 40px rgba(99, 102, 241, 0.2)',
                  },
                }}
              >
                <ContentCut sx={{ fontSize: 60, color: '#6366f1', mb: 2 }} />
                <Typography variant="h6" fontWeight="700" color="#1a237e">
                  Hair Services
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={6} md={3}>
              <Card
                sx={{
                  p: 3,
                  textAlign: 'center',
                  borderRadius: 3,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 12px 40px rgba(139, 92, 246, 0.2)',
                  },
                }}
              >
                <Face sx={{ fontSize: 60, color: '#8b5cf6', mb: 2 }} />
                <Typography variant="h6" fontWeight="700" color="#1a237e">
                  Beauty Services
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={6} md={3}>
              <Card
                sx={{
                  p: 3,
                  textAlign: 'center',
                  borderRadius: 3,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 12px 40px rgba(236, 72, 153, 0.2)',
                  },
                }}
              >
                <Spa sx={{ fontSize: 60, color: '#ec4899', mb: 2 }} />
                <Typography variant="h6" fontWeight="700" color="#1a237e">
                  Spa & Wellness
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={6} md={3}>
              <Card
                sx={{
                  p: 3,
                  textAlign: 'center',
                  borderRadius: 3,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 12px 40px rgba(245, 158, 11, 0.2)',
                  },
                }}
              >
                <ShoppingBag sx={{ fontSize: 60, color: '#f59e0b', mb: 2 }} />
                <Typography variant="h6" fontWeight="700" color="#1a237e">
                  Products
                </Typography>
              </Card>
            </Grid>
          </Grid>
        </Box>

        {/* CTA Section */}
        <Card
          sx={{
            background: 'linear-gradient(135deg, #0A2A66 0%, #1a237e 50%, #6366f1 100%)',
            borderRadius: 4,
            overflow: 'hidden',
            position: 'relative',
          }}
        >
          <CardContent sx={{ textAlign: 'center', py: 8, position: 'relative', zIndex: 1 }}>
            <EmojiEvents sx={{ fontSize: 80, color: 'white', mb: 3 }} />
            <Typography variant="h3" fontWeight="bold" color="white" gutterBottom>
              Ready to Look & Feel Amazing?
            </Typography>
            <Typography variant="h6" color="white" sx={{ opacity: 0.9, mb: 4, maxWidth: 600, mx: 'auto' }}>
              Join thousands of happy customers who trust NIStylez for their beauty and wellness needs
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                size="large"
                onClick={() => navigate('/')}
                sx={{
                  px: 5,
                  py: 2,
                  fontSize: '1.1rem',
                  fontWeight: 'bold',
                  background: 'white',
                  color: '#1a237e',
                  '&:hover': {
                    background: '#f8fafc',
                    transform: 'scale(1.05)',
                  },
                }}
              >
                Start Booking
              </Button>
              <Button
                variant="outlined"
                size="large"
                startIcon={<Support />}
                onClick={() => window.location.href = 'mailto:nistylezaustralia@gmail.com'}
                sx={{
                  px: 5,
                  py: 2,
                  fontSize: '1.1rem',
                  fontWeight: 'bold',
                  borderColor: 'white',
                  color: 'white',
                  borderWidth: 2,
                  '&:hover': {
                    borderColor: 'white',
                    background: 'rgba(255,255,255,0.1)',
                    borderWidth: 2,
                  },
                }}
              >
                Contact Support
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Container>

      {/* Bottom Navigation */}
      <BottomNav />
    </Box>
  );
};

export default AboutUs;
